<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <h1 class="rsvl-title">{{ title }}</h1>

          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <template v-else>
            <div class="rsvl-grid">
              <div
                v-for="r in filteredReservations"
                :key="r.id"
                class="rsvl-card"
              >
                <div class="rsvl-photo">
                  <img
                    v-if="r.play && r.play.image"
                    :src="r.play.image"
                    :alt="r.play.title"
                  />
                  <span v-else class="rsvl-photo-fallback">
                    <v-icon size="44" color="#ffffff">mdi-drama-masks</v-icon>
                  </span>
                </div>

                <div class="rsvl-info">
                  <div class="rsvl-field">
                    <div class="rsvl-label">OBRA</div>
                    <div class="rsvl-value">{{ (r.play && r.play.title || 'Obra').toUpperCase() }}</div>
                  </div>
                  <div class="rsvl-field">
                    <div class="rsvl-label">FECHA DE LA FUNCIÓN</div>
                    <div class="rsvl-value">{{ formatDate(r.displayDate) }}</div>
                  </div>
                  <div class="rsvl-field">
                    <div class="rsvl-label">COMPAÑÍA</div>
                    <div class="rsvl-value">{{ companyName(r).toUpperCase() }}</div>
                  </div>
                  <!-- Quién hizo la reserva: para la administración y la compañía. -->
                  <template v-if="isManager">
                    <div class="rsvl-field">
                      <div class="rsvl-label">EMPRESA CLIENTE</div>
                      <div class="rsvl-value">{{ clientCompany(r).toUpperCase() }}</div>
                    </div>
                    <div class="rsvl-field">
                      <div class="rsvl-label">CONTACTO</div>
                      <div class="rsvl-value">{{ clientContact(r) }}</div>
                    </div>
                    <div class="rsvl-field">
                      <div class="rsvl-label">CORREO</div>
                      <div class="rsvl-value rsvl-email">{{ clientEmail(r) }}</div>
                    </div>
                  </template>
                  <!-- Comentario del cliente al reservar (servicios extra, más
                       funciones…): lo ven la administración y el propio cliente. -->
                  <div v-if="(isAdmin || isClient) && r.clientComment" class="rsvl-field">
                    <div class="rsvl-label">{{ isAdmin ? 'COMENTARIO DEL CLIENTE' : 'TU COMENTARIO (SERVICIOS EXTRA)' }}</div>
                    <div class="rsvl-value rsvl-comment">{{ r.clientComment }}</div>
                  </div>
                  <div class="rsvl-field">
                    <div class="rsvl-label">ESTADO DE LA COMPRA</div>
                    <div class="rsvl-value rsvl-state">
                      <span>{{ purchaseLabel(r) }}</span>
                      <span class="rsvl-dot" :style="{ background: statusColor(r.status) }"></span>
                    </div>
                  </div>

                  <a class="rsvl-detail-btn" :href="`/reserva/${r.id}/`">
                    DETALLES DE MI RESERVA
                  </a>
                </div>
              </div>
            </div>

            <v-alert
              v-if="filteredReservations.length === 0"
              type="info"
              class="mt-4"
            >
              No se encontraron reservas.
            </v-alert>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchMyReservations } from "@/services/teatroApi";
import { statusInfo } from "./reservationStatus";

// Etiqueta orientada a la compra que se muestra en la tarjeta.
const PURCHASE_LABELS = {
  pending: "POR CONFIRMAR",
  pending_availability: "POR CONFIRMAR",
  date_requested: "POR CONFIRMAR",
  date_confirmed: "RESERVADO",
  contract_pending: "CONTRATOS PENDIENTES",
  deposit_pending: "FACTURA DE RESERVA",
  deposit_paid: "20% ABONADO",
  balance_invoice_pending: "SALDO FACTURADO",
  balance_pending: "SALDO PENDIENTE",
  show_completed: "FUNCIÓN REALIZADA",
  balance_paid: "PAGADO 100%",
  company_invoice_pending: "FACTURA CÍA. PENDIENTE",
  company_payment_pending: "PAGO A LA CÍA. PENDIENTE",
  company_paid: "CÍA. PAGADA",
  cancelled: "CANCELADA",
};

const STATUS_GROUPS = {
  por_confirmar: ["pending", "pending_availability", "date_requested"],
  confirmadas: ["date_confirmed"],
  contratadas: [
    "contract_pending",
    "deposit_pending",
    "deposit_paid",
    "balance_invoice_pending",
    "balance_pending",
    "show_completed",
    "balance_paid",
    "company_invoice_pending",
    "company_payment_pending",
    "company_paid",
  ],
  rechazadas: ["cancelled"],
};

const GROUP_TITLES = {
  por_confirmar: "Reservas por confirmar",
  confirmadas: "Reservas confirmadas",
  contratadas: "Reservas contratadas",
  rechazadas: "Reservas rechazadas",
};

export default {
  name: "ReservationList",
  props: {
    // One of: por_confirmar | confirmadas | contratadas | rechazadas.
    // Empty string shows every reservation the user can see.
    statusGroup: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      reservations: [],
      loading: true,
      error: "",
    };
  },
  computed: {
    title() {
      if (this.statusGroup && GROUP_TITLES[this.statusGroup]) {
        return GROUP_TITLES[this.statusGroup];
      }
      return this.isManager ? "Gestión de reservas" : "Mis reservas";
    },
    isAdmin() {
      return this.$store.state.is_admin;
    },
    isClient() {
      return this.$store.state.role === "client";
    },
    isManager() {
      return (
        this.$store.state.is_admin ||
        this.$store.state.role === "theater_member"
      );
    },
    filteredReservations() {
      const statuses = STATUS_GROUPS[this.statusGroup];
      if (!statuses) return this.reservations;
      return this.reservations.filter((r) =>
        statuses.includes(String(r.status || "").toLowerCase())
      );
    },
  },
  async created() {
    await this.loadAuth();
    await this.loadReservations();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error("No se pudo cargar la sesión", e);
      }
    },
    async loadReservations() {
      this.loading = true;
      this.error = "";
      try {
        this.reservations = await fetchMyReservations();
      } catch (e) {
        this.error = "No se pudieron cargar las reservas.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    companyName(r) {
      return (r.play && r.play.company && r.play.company.name) || "—";
    },
    // Datos de quien hizo la reserva (cuenta del cliente; la reserva de respaldo).
    clientCompany(r) {
      return (r.client && r.client.company && r.client.company.name) || "—";
    },
    clientContact(r) {
      return (r.client && r.client.fullName) || r.name || "—";
    },
    clientEmail(r) {
      return r.email || (r.client && r.client.email) || "—";
    },
    purchaseLabel(r) {
      const key = String(r.status || "").toLowerCase();
      return PURCHASE_LABELS[key] || r.statusDisplay || "—";
    },
    formatDate(iso) {
      if (!iso) return "-";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      if (y && m && d) return `${d}/${m}/${y}`;
      const dt = new Date(iso);
      if (isNaN(dt.getTime())) return iso;
      const dd = String(dt.getDate()).padStart(2, "0");
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      return `${dd}/${mm}/${dt.getFullYear()}`;
    },
    statusColor(status) {
      return statusInfo(status).color;
    },
  },
};
</script>

<style scoped>
.rsvl-title {
  text-align: center;
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 0 0 30px;
}

.rsvl-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 26px;
}

.rsvl-card {
  display: flex;
  align-items: center;
  gap: 22px;
  background: rgba(12, 7, 7, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 22px;
  padding: 22px 26px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.5);
}

.rsvl-photo {
  flex: 0 0 auto;
  width: 132px;
  height: 132px;
  border-radius: 50%;
  overflow: hidden;
  background: #1c1412;
  border: 1px solid rgba(255, 255, 255, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
}

.rsvl-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rsvl-photo-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}

.rsvl-info {
  flex: 1 1 auto;
  min-width: 0;
}

.rsvl-field {
  margin-bottom: 8px;
}

.rsvl-label {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
}

.rsvl-value {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
}

.rsvl-comment {
  letter-spacing: 0;
  white-space: pre-line;
  word-break: break-word;
}

.rsvl-email {
  letter-spacing: 0;
  word-break: break-all;
}

.rsvl-state {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rsvl-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex: 0 0 auto;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.4);
}

.rsvl-detail-btn {
  display: inline-block;
  margin-top: 12px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  text-decoration: none;
  border-radius: 6px;
  padding: 9px 18px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rsvl-detail-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.4);
}

@media (max-width: 900px) {
  .rsvl-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .rsvl-card {
    flex-direction: column;
    text-align: center;
  }
}
</style>
